import { makeAutoObservable, runInAction } from "mobx";
import FirebaseService from "./FirebaseService";
import {
  collection,
  query,
  where,
  orderBy,
  limit,
  getDocs,
  Timestamp
} from "firebase/firestore";
import posthog from "posthog-js";

interface SectionInsight {
  section_number: number;
  insight: string;
}

export interface AICoachResponse {
  overall_feedback: string;
  section_insights: SectionInsight[];
  nutrition_hydration_feedback: string;
  gear_feedback: string;
  risks: string[];
  actionable_adjustments: string[];
}

export interface Analysis {
  id?: string;
  planId: string;
  userId: string;
  response: AICoachResponse;
  createdAt: Date;
  updatedAt: Date;
}

export default class AnalysisService {
  private _firebaseService: FirebaseService;
  private _isLoading: boolean = false;
  private _error: string | null = null;
  private _currentAnalysis: Analysis | null = null;

  constructor(firebaseService: FirebaseService) {
    this._firebaseService = firebaseService;
    makeAutoObservable(this);
  }

  get isLoading(): boolean {
    return this._isLoading;
  }

  get error(): string | null {
    return this._error;
  }

  get currentAnalysis(): Analysis | null {
    return this._currentAnalysis;
  }

  /**
   * Call the getRaceStrategy cloud function to generate an AI analysis
   * @param userId User ID
   * @param planId Plan ID
   * @param plan The plan object created with getPromptJson
   * @param language User's language preference
   * @param query "initial" or "full"
   * @returns The generated analysis or null if error
   */
  async getRaceStrategy(
    userId: string,
    planId: string,
    plan: any,
    language: string,
    query: "initial" | "full" = "full"
  ): Promise<Analysis | null> {
    this._isLoading = true;
    this._error = null;

    try {
      // Get the Firebase auth token
      const currentUser = this._firebaseService.auth.currentUser;
      if (!currentUser) {
        throw new Error("User not authenticated");
      }

      const token = await currentUser.getIdToken(true);
      
      posthog.capture("analysis_request_started", {
        user_id: userId,
        plan_id: planId,
        query_type: query
      });

      // Make HTTP request to the cloud function
      const functionsBaseUrl = process.env.REACT_APP_FUNCTIONS_BASE_URL || "https://us-central1-trail-tools.cloudfunctions.net";
      const response = await fetch(
        `${functionsBaseUrl}/getRaceStrategy`,
        {
          method: "POST",
          headers: {
            "Content-Type": "application/json",
            "Authorization": `Bearer ${token}`
          },
          body: JSON.stringify({
            planId,
            plan,
            language,
            query
          })
        }
      );

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.error || `HTTP error! status: ${response.status}`);
      }

      const aiResponse: AICoachResponse = await response.json();
      const now = new Date();


      const analysis: Analysis = {
        id: "",
        planId,
        userId,
        response: aiResponse,
        createdAt: now,
        updatedAt: now
      };

      runInAction(() => {
        this._currentAnalysis = analysis;
        this._isLoading = false;
      });

      return analysis;
    } catch (error) {
      console.error("Error getting race strategy:", error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture("error", {
        source: "getRaceStrategy",
        error: (error as Error).message,
        user_id: userId,
        plan_id: planId
      });
      return null;
    }
  }

  /**
   * Load the most recent analysis for a user and plan
   * @param userId User ID
   * @param planId Plan ID
   * @returns The most recent analysis or null if not found
   */
  async loadMostRecentAnalysis(
    userId: string,
    planId: string
  ): Promise<Analysis | null> {
    this._isLoading = true;
    this._error = null;

    try {
      const db = this._firebaseService.db;
      const analysisRef = collection(db, "analysis");
      const q = query(
        analysisRef,
        where("userId", "==", userId),
        where("planId", "==", planId),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const doc = snapshot.docs[0];
        const data = doc.data();
        const analysis: Analysis = {
          id: doc.id,
          planId: data.planId,
          userId: data.userId,
          response: JSON.parse(data.response) as AICoachResponse,
          createdAt: data.createdAt?.toDate(),
          updatedAt: data.updatedAt?.toDate()
        };

        posthog.capture("analysis_loaded", {
          analysis_id: doc.id,
          user_id: userId,
          plan_id: planId
        });

        runInAction(() => {
          this._currentAnalysis = analysis;
          this._isLoading = false;
        });

        return analysis;
      }

      runInAction(() => {
        this._currentAnalysis = null;
        this._isLoading = false;
      });

      return null;
    } catch (error) {
      console.error("Error loading most recent analysis:", error);
      runInAction(() => {
        this._error = (error as Error).message;
        this._isLoading = false;
      });
      posthog.capture("error", {
        source: "loadMostRecentAnalysis",
        error: (error as Error).message,
        user_id: userId,
        plan_id: planId
      });
      return null;
    }
  }

  /**
   * Clear the current analysis from state
   */
  clearCurrentAnalysis(): void {
    this._currentAnalysis = null;
    this._error = null;
  }

  /**
   * Check if the user can request a new analysis based on rate limiting (15 minutes per plan)
   * @param userId User ID
   * @param planId Plan ID
   * @returns Object with canRequest boolean and minutesUntilAvailable number
   */
  async checkRateLimit(userId: string, planId: string): Promise<{ canRequest: boolean; minutesUntilAvailable: number }> {
    try {
      const db = this._firebaseService.db;
      const analysisRef = collection(db, "analysis");
      const q = query(
        analysisRef,
        where("userId", "==", userId),
        where("planId", "==", planId),
        orderBy("createdAt", "desc"),
        limit(1)
      );

      const snapshot = await getDocs(q);

      if (!snapshot.empty) {
        const lastAnalysis = snapshot.docs[0].data();
        const lastCreatedAt = lastAnalysis.createdAt?.toDate();

        if (lastCreatedAt) {
          const now = new Date();
          const fifteenMinutesAgo = new Date(now.getTime() - 15 * 60 * 1000);

          if (lastCreatedAt > fifteenMinutesAgo) {
            const timeDiff = lastCreatedAt.getTime() - fifteenMinutesAgo.getTime();
            const minutesUntilAvailable = Math.ceil(timeDiff / (60 * 1000));
            return { canRequest: false, minutesUntilAvailable };
          }
        }
      }

      return { canRequest: true, minutesUntilAvailable: 0 };
    } catch (error) {
      console.error("Error checking rate limit:", error);
      return { canRequest: true, minutesUntilAvailable: 0 };
    }
  }

  /**
   * Get usage limits based on subscription type
   * @param subscriptionType The user's subscription type
   * @returns Object with limit and period
   */
  getUsageLimits(subscriptionType: "lifetime" | "monthly" | "annual" | "special" | null): { limit: number; period: "month" | "year" } {
    switch (subscriptionType) {
      case "monthly":
        return { limit: 3, period: "month" };
      case "annual":
      case "special":
        return { limit: 50, period: "year" };
      case "lifetime":
        return { limit: 100, period: "year" };
      default:
        return { limit: 0, period: "month" };
    }
  }

  /**
   * Check if the user has reached their usage limit
   * @param userId User ID
   * @param subscriptionType The user's subscription type
   * @returns Object with canRequest boolean and usageCount number
   */
  async checkUsageLimit(userId: string, subscriptionType: "lifetime" | "monthly" | "annual" | "special" | null): Promise<{ canRequest: boolean; usageCount: number; limit: number }> {
    try {
      const limits = this.getUsageLimits(subscriptionType);

      if (limits.limit === 0) {
        return { canRequest: false, usageCount: 0, limit: 0 };
      }

      const db = this._firebaseService.db;
      const analysisRef = collection(db, "analysis");

      // Calculate the start date based on the period
      const now = new Date();
      const startDate = new Date();
      if (limits.period === "month") {
        startDate.setMonth(now.getMonth() - 1);
      } else {
        startDate.setFullYear(now.getFullYear() - 1);
      }

      const q = query(
        analysisRef,
        where("userId", "==", userId),
        where("createdAt", ">=", Timestamp.fromDate(startDate))
      );

      const snapshot = await getDocs(q);
      const usageCount = snapshot.size;

      return {
        canRequest: usageCount < limits.limit,
        usageCount,
        limit: limits.limit
      };
    } catch (error) {
      console.error("Error checking usage limit:", error);
      return { canRequest: true, usageCount: 0, limit: 0 };
    }
  }
}
